import React, { useContext } from "react";
import { Box, Heading, SimpleGrid, Card, CardHeader, CardBody, Text, Badge, Button, HStack, VStack, Select, useToast, Input, FormControl, FormLabel, Divider } from "@chakra-ui/react";
import { Link, Navigate } from "react-router-dom";
import AuthContext from "../../contexts/Auth";
import client from "../../helpers/Client";

export default function AdminHome() {
  const auth = useContext(AuthContext);
  const isSuperUser = auth.data?.user?.is_superuser;
  const toast = useToast();
  const userRoles = {
    superuser: !!auth.data?.user?.is_superuser,
    manager: !!auth.data?.user?.employee?.is_manager,
    servicer: !!auth.data?.user?.is_servicer,
    technical: !!auth.data?.user?.is_technical,
    reseller: !!auth.data?.user?.is_reseller,
    employee: !!auth.data?.user?.is_employee,
  };
  const [previewRole, setPreviewRole] = React.useState("auto");
  const [disabledRoutes, setDisabledRoutes] = React.useState(new Set());

  // Hooks must be called unconditionally; guard side-effects inside the effect

  const sections = [
    { title: "Kullanıcılar", description: "Kullanıcı hesapları ve roller", to: "/admin/kullanicilar/" },
    { title: "Personellar", description: "Personel yönetimi (Admin)", to: "/admin/calisanlar/" },
    { title: "Bayi", description: "Bayi ve ekipler", to: "/bayi/" },
    { title: "Takımlar", description: "Takım ve üyelikler", to: "/takımlar/" },
    { title: "Müşteriler", description: "Müşteri kayıtları", to: "/musteriler/" },
    { title: "Müşteriler (Admin)", description: "Müşteri yönetimi", to: "/admin/musteriler/" },
    { title: "Bayiler (Admin)", description: "Bayi yönetimi", to: "/admin/bayiler/" },
    { title: "İş Emri", description: "İş emirleri ve durumlar", to: "/is-emri/" },
    { title: "Servis Kayıtları", description: "Servis işlemleri", to: "/servis/" },
    { title: "Projeler", description: "Proje listesi ve yönetim", to: "/admin/projeler/liste/" },
    { title: "Üniteler", description: "Ünite listesi ve yönetim", to: "/admin/uniteler/" },
    { title: "Notlar", description: "Sistem notları", to: "/admin/notlar/" },
    { title: "Yerleşimler", description: "Ülke/Şehir/İlçe", to: "/admin/yerlesimler/" },
    { title: "Hareket Logları", description: "Ticket/Task/Servis kayıt logları", to: "/admin/loglar/" },
    { title: "İş/Arıza Tipleri", description: "Tip adlarını düzenle, görünürlüğü yönet", to: "/admin/is-tipleri/" },
    { title: "Rol → Sayfa Erişimi", description: "Rollere göre görünen sayfaları aç/kapat", to: "/admin/rol-sayfalari/" },
    { title: "Rol → Liste Görünümü", description: "Rol seç, sayfaları listeden aç/kapat", to: "/admin/rol-listesi/" },
  ];

  // Fetch disabled routes for user roles (deny list). Preview role overrides.
  React.useEffect(() => {
    const load = async () => {
      if (!isSuperUser) { setDisabledRoutes(new Set()); return; }
      try {
        const roles = previewRole === "auto"
          ? Object.entries(userRoles).filter(([_, v]) => v).map(([k]) => k)
          : [previewRole];
        if (roles.length === 0) { setDisabledRoutes(new Set()); return; }
        // Fetch all disabled entries once and filter client-side
        const { data } = await client.get(`/config/role-pages/?enabled=false`);
        const items = Array.isArray(data?.results) ? data.results : (Array.isArray(data) ? data : []);
        const disabled = items.filter(it => roles.includes(it.role_code)).map(it => it.route);
        setDisabledRoutes(new Set(disabled));
      } catch (e) {
        // Non-blocking; show everything on error
        toast({ title: "Rol erişim kuralları alınamadı", status: "warning" });
        setDisabledRoutes(new Set());
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewRole, isSuperUser]);

  const visibleSections = sections.filter(s => !disabledRoutes.has(s.to));
  const [uploading, setUploading] = React.useState(false);
  const fileInputRef = React.useRef(null);

  const backupNow = async () => {
    try {
      await client.post("/admin/db/backup/");
      toast({ title: "Yedek alındı", status: "success" });
    } catch (e) {
      toast({ title: "Yedek alınamadı", description: e?.message || "Hata", status: "error" });
    }
  };

  const exportNow = async () => {
    try {
      const resp = await client.get("/admin/db/export/", { responseType: "blob" });
      const blob = new Blob([resp.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "db-backup.dump";
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast({ title: "Dışa aktarılamadı", description: e?.message || "Hata", status: "error" });
    }
  };

  const importNow = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await client.post("/admin/db/import/", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast({ title: "İçe aktarıldı", status: "success" });
    } catch (e) {
      toast({ title: "İçe aktarılamadı", description: e?.message || "Hata", status: "error" });
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  return (
    <Box p={6}>
      <Heading size="md" mb={2}>Admin Sayfası</Heading>
      <HStack mb={4} spacing={3}>
        {isSuperUser ? (
          <>
            <Select size="sm" value={previewRole} onChange={(e) => setPreviewRole(e.target.value)} w="220px">
              <option value="auto">Rol Önizleme: Otomatik</option>
              <option value="manager">Yönetici</option>
              <option value="servicer">Servisçi</option>
              <option value="technical">Teknik</option>
              <option value="reseller">Bayi</option>
              <option value="employee">Personel</option>
            </Select>
            <Button as={Link} to="/admin/rol-matris/" size="sm" colorScheme="purple">Rol Matrisini Yönet</Button>
          </>
        ) : (
          <Navigate to="/ana-sayfa/" replace />
        )}
      </HStack>
      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
        {visibleSections.map((s) => (
          <Card key={s.title} variant="outline">
            <CardHeader>
              <Heading size="sm">{s.title} <Badge ml={2} colorScheme="purple">Admin</Badge></Heading>
            </CardHeader>
            <CardBody>
              <Text mb={4}>{s.description}</Text>
              <Button as={Link} to={s.to} colorScheme="blue" size="sm">Yönet</Button>
            </CardBody>
          </Card>
        ))}

        {isSuperUser && (
          <Card variant="outline">
            <CardHeader>
              <Heading size="sm">Veritabanı Ayarları</Heading>
            </CardHeader>
            <CardBody>
              <VStack align="stretch" spacing={3}>
                <Text fontSize="sm" color="gray.600">
                  Varsayılan DB ayarları kullanılır; host/kullanıcı girişi gerekmez.
                </Text>
                <Divider />
                <HStack spacing={2} wrap="wrap">
                  <Button size="sm" colorScheme="blue" onClick={backupNow}>Yedek Al</Button>
                  <Button size="sm" onClick={exportNow}>Dışa Aktar (indir)</Button>
                  <Button
                    size="sm"
                    isLoading={uploading}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    İçe Aktar
                  </Button>
                </HStack>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="*/*"
                  style={{ display: "none" }}
                  onChange={(e) => importNow(e.target.files?.[0])}
                />
              </VStack>
            </CardBody>
          </Card>
        )}
      </SimpleGrid>
    </Box>
  );
}
